import React, {useMemo, useState} from 'react'
import icons from '../../markons/markons'
import {
    resolveRoute,
    submitServerRedirect
} from '../helpers/ajax'
import * as kb from '../kb'
import ButtonRow from './ButtonRow'
import {GridCol, GridForm, GridRow, GridRowSingleCol, GridSection} from './grid-form'
import {arrayDeleteIndex, iterableIncludes} from '../util/collections'
import {
    ActionButtonIcon,
    CancelLinkRoute,
    DeleteButton,
    DeleteIconLink,
    SaveButton
} from './SaveSubmitButton'
import AioText from './aio-inputs/AioText'
import AioTextArea from './aio-inputs/AioTextArea'
import AioCheckboxList from './aio-inputs/AioCheckboxList'
import type {PackageFee} from '../data/package-data';
import { usePackageFees, usePackageVehicles} from '../data/package-data'
import {useStore} from '../helpers/react-form'
import AioSelect from './aio-inputs/AioSelect'
import AioDollar from './aio-inputs/AioDollar'
import useEvent from '../hooks/useEvent'
import type {CheckboxOption} from '../types/shared-input-types'

interface PackageFormProps {
    formData: FormData;
}

type FormData = {
    id: string;
    name: string;
    description: string;
    vehicles: PackageFormVehicle[];
    fees: number[];
}

type PackageFormVehicle = {
    vehicle_id: number,
    price: string
}

function fee2opt(fee: PackageFee) {
    const label = `${fee.name} (${fee.type === 'FIXED' ? kb.formatCurrency(fee.amount, false) : kb.formatFeePercent(fee.amount)})`

    return {
        value: fee.id,
        label,
    }
}

export default function PackageForm({formData}: PackageFormProps) {
    const store = useStore(formData)
    const [saving, setSaving] = useState(false)

    const vehiclesQuery = usePackageVehicles()
    const feesQuery = usePackageFees()

    const fees = useMemo<CheckboxOption<number>[]>(() => feesQuery.data!.filter(f => f.amount >= 0).map(fee2opt), [feesQuery.data])
    const discounts = useMemo<CheckboxOption<number>[]>(() => feesQuery.data!.filter(f => f.amount < 0).map(fee2opt), [feesQuery.data])
    const vehicles = useMemo<[number, string][]>(() => vehiclesQuery.data!.map(v => [Number(v.id), `${v.name} (${v.code})`]), [vehiclesQuery.data])
    const selectedVehicleIds = useMemo(() => new Set((store.data.vehicles ?? []).map(v => Number(v.vehicle_id))), [store.data.vehicles])

    const deleteVehicle = (idx: number) => () => {
        store.set('vehicles', v => arrayDeleteIndex(v, idx))
    }

    const addVehicle = useEvent(() => {
        store.set('vehicles', currVehicles => [...currVehicles, {
            vehicle_id: vehicles.find(v1 => !currVehicles.some(v2 => v1[0] == v2.vehicle_id))?.[0] ?? null,
            price: '',
        }])
    })

    const handleDelete = useEvent(() => {
        setSaving(true)
        submitServerRedirect(resolveRoute('packages.delete'), {id: formData.id})
            .catch(() => {
                setSaving(false)
            })
    })

    const handleSave = () => {
        setSaving(true)
        submitServerRedirect(resolveRoute('packages.store'), store.data)
            .catch(() => {
                setSaving(false)
            })
    }

    return (
        <GridForm>
            <GridSection>
                <GridRowSingleCol>
                    <AioText label="Name" {...store.input('name')} />
                </GridRowSingleCol>
                <GridRowSingleCol>
                    <AioTextArea label="Description" {...store.input('description')} />
                </GridRowSingleCol>
                {store.get('vehicles', []).map((vehicle, vi) => {
                    const vehicleOptions = vehicles.filter(v => v[0] == vehicle.vehicle_id || !iterableIncludes(selectedVehicleIds, v[0]))

                    return (
                        <GridRow key={vi}>
                            <GridCol>
                                <AioSelect icon={icons.faCar}
                                    placeholder="— Please Select —"
                                    label="Vehicle"
                                    options={vehicleOptions} {...store.input(['vehicles', vi, 'vehicle_id'])} />
                            </GridCol>
                            <GridCol>
                                <AioDollar label="Price" {...store.input(['vehicles', vi, 'price'])} />
                            </GridCol>
                            <GridCol shrink center>
                                <DeleteIconLink onClick={deleteVehicle(vi)} />
                            </GridCol>
                        </GridRow>
                    )
                })}
                <GridRowSingleCol>
                    <ActionButtonIcon onClick={addVehicle}
                        disabled={vehicles.length === store.data.vehicles?.length}
                        icon={icons.ionAndroidAddCircle}
                        className="btn-new">Add Vehicle</ActionButtonIcon>
                </GridRowSingleCol>

                <GridRow>
                    <GridCol minWidth={250}>
                        <AioCheckboxList {...store.checkboxList('fees')} label="Fees"
                            options={fees}
                            columnWidth={250}
                            noItems={feesQuery.isFetching ? "Loading..." : undefined} />
                    </GridCol>
                    <GridCol minWidth={250}>
                        <AioCheckboxList {...store.checkboxList('fees')} label="Discounts"
                            options={discounts}
                            columnWidth={250}
                            noItems={feesQuery.isFetching ? "Loading..." : undefined} />
                    </GridCol>
                </GridRow>
            </GridSection>

            <ButtonRow>
                <ButtonRow.Left>
                    <SaveButton processing={saving} onClick={handleSave} />
                </ButtonRow.Left>
                <ButtonRow.Right>
                    <CancelLinkRoute route="packages.index">Discard Changes</CancelLinkRoute>
                    {formData.id ? <DeleteButton disabled={saving} onClick={handleDelete} /> : null}
                </ButtonRow.Right>
            </ButtonRow>
        </GridForm>
    )
}

